"use client"

import { useEffect } from "react"
import { useRouter } from "next/navigation"
import {
  ensureChatbotEmbed,
  type ChatbotEmbedLocale,
} from "@/lib/atirame-chatbot-embed"
import { readStoredLandingLocale } from "@/lib/marketing-locale"
import {
  ONBOARDING_DONE_EVENT,
  ONBOARDING_FINISH_EVENT,
  WIDGET_ONBOARDING_PARAM,
  clearWidgetOnboarding,
  isWidgetOnboardingActive,
  requestWidgetOnboarding,
  startWidgetOnboarding,
  type OnboardingFinishDetail,
} from "@/lib/onboarding-widget"

/**
 * Puts the chatbot widget on a page that is not a marketing page, and hands it
 * the sign-up wizard when one is pending.
 *
 * `/pathway` never carried the widget: it is the windowed app shell, and the
 * bubble belonged to the landing pages. It needs it now because registration
 * finishes here, in the OTP window, and the business questions that follow are
 * asked in the bubble rather than on a separate page.
 */

/**
 * `/pathway` renders in Persian unless the visitor already chose otherwise on
 * the international landing page, which is the only place a choice is made.
 */
function resolveWidgetLocale(): ChatbotEmbedLocale {
  return readStoredLandingLocale() ?? "fa"
}

/**
 * `?onboarding=1` (the old `/onboarding` URL forwards here) asks for the
 * wizard. The flag is dropped from the address once read so a reload does not
 * ask again; the session flag already covers resuming. `event` stays: the
 * widget reads the fair's name from it.
 */
function consumeOnboardingParam(): boolean {
  const url = new URL(window.location.href)
  if (url.searchParams.get(WIDGET_ONBOARDING_PARAM) !== "1") return false
  url.searchParams.delete(WIDGET_ONBOARDING_PARAM)
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash)
  return true
}

export function OnboardingWidgetHost() {
  const router = useRouter()

  useEffect(() => {
    let cancelled = false

    void ensureChatbotEmbed(resolveWidgetLocale()).catch((error) => {
      if (!cancelled) {
        console.error("[OnboardingWidgetHost] Failed to load chatbot widget:", error)
      }
    })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    // The wizard's own state lives in a server session, so re-entering it after
    // a reload is just a matter of asking the widget to start again — it picks
    // up at whatever step the session is on.
    if (consumeOnboardingParam()) requestWidgetOnboarding()
    if (!isWidgetOnboardingActive()) return

    const stop = startWidgetOnboarding()
    const onDone = () => clearWidgetOnboarding()
    window.addEventListener(ONBOARDING_DONE_EVENT, onDone)

    return () => {
      stop()
      window.removeEventListener(ONBOARDING_DONE_EVENT, onDone)
    }
  }, [])

  useEffect(() => {
    // "Done" under the finished wizard: this page can open the chatbot
    // settings window itself, so it does, instead of the widget reloading it.
    const onFinish = (event: Event) => {
      event.preventDefault()
      clearWidgetOnboarding()
      const { domainId } = ((event as CustomEvent<OnboardingFinishDetail>).detail ?? {})
      const params = new URLSearchParams({ window: "domain-management" })
      if (domainId) params.set("domainId", domainId)
      router.push(`/pathway?${params.toString()}`)
    }
    window.addEventListener(ONBOARDING_FINISH_EVENT, onFinish)
    return () => window.removeEventListener(ONBOARDING_FINISH_EVENT, onFinish)
  }, [router])

  return null
}
